import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { SvgIconComponent } from '../../../../../../../components/svg-icon/svg-icon.component';
import { GpdButtonComponent } from '../../../../../../../components/gpd-button/gpd-button.component';

import { MilestoneProjectConfigNodeComponent } from '../../../components/project-config-node/types/milestone-project-config-node/milestone-project-config-node.component';
import { DxSortableComponent } from 'devextreme-angular/ui/sortable';
import { DxScrollViewComponent } from 'devextreme-angular';
import { ReorderEvent, DragChangeEvent } from 'devextreme/ui/sortable';

import validationEngine from 'devextreme/ui/validation_engine';
import { DxValidatorComponent, DxValidatorModule } from 'devextreme-angular/ui/validator';
import { MultiStepFormComponent } from '../../../../../../../components/multi-step-form/multi-step-form.component';
import { ProjectConfigNodeComponent } from '../../../components/project-config-node/project-config-node.component';
import { PhaseProjectConfigNodeComponent } from '../../../components/project-config-node/types/phase-project-config-node/phase-project-config-node.component';
import { TaskPackageProjectConfigNodeComponent } from '../../../components/project-config-node/types/task-package-project-config-node/task-package-project-config-node.component';
import { Milestone, Phase, TaskPackage } from '../../../../../../../interfaces/entities/project-configuration';
import { SafeHtmlFilter } from '../../../../../../../pipes/safeHTML.filter';
import { SharedPipesModule } from '../../../../../../../pipes/shared.pipes.module';

interface EditState {
  type: 'phase' | 'milestone' | 'task';
  phaseIndex: number;
  milestoneIndex?: number;
  taskIndex?: number;
  isNew?: boolean;
  originalValue?: Phase | Milestone | TaskPackage;
}

@Component({
    selector: 'app-setup-summary-step',
    imports: [
        CommonModule,
        SvgIconComponent,
        GpdButtonComponent,
        ProjectConfigNodeComponent,
        PhaseProjectConfigNodeComponent,
        MilestoneProjectConfigNodeComponent,
        TaskPackageProjectConfigNodeComponent,
        DxSortableComponent,
        DxScrollViewComponent,
        DxValidatorComponent,
        DxValidatorModule,
        SharedPipesModule,
    ],
    templateUrl: './setup-summary-step.component.html',
    styleUrl: './setup-summary-step.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SetupSummaryStepComponent {
  @Input() phases: Phase[] = [];
  @Input() multiStepForm?: MultiStepFormComponent;
  @Output() phasesChange = new EventEmitter<Phase[]>();

  selectedPhaseIndex: number | null = 0;
  selectedMilestoneIndex: number | null = 0;
  editState: EditState | null = null;

  summaryValidationCallbacks: Array<() => void> = [];

  constructor(private cdr: ChangeDetectorRef) {}

  milestoneAdapterConfig = {
    getValue: () => {
      return this.summaryValidationCallback();
    },
    validationRequestsCallbacks: this.summaryValidationCallbacks,
  };

  summaryValidationCallback = () => {
    // Collect all milestones from all phases
    const allTaskPackages: TaskPackage[] = [];
    for (const phase of this.phases) {
      if (phase.milestones?.length > 0) {
        for (const milestone of phase.milestones) {
          if (milestone.taskPackages?.length > 0) {
            allTaskPackages.push(...milestone.taskPackages);
          }
        }
      }
    }

    if (allTaskPackages.length === 0) {
      return false;
    }

    const allMilestones: Milestone[] = [];

    for (const phase of this.phases) {
      if (phase.milestones?.length > 0) {
        allMilestones.push(...phase.milestones);
      }
    }

    return true;
  };

  onPhaseClick(phaseIndex: number) {
    // Check if there's an invalid edit in progress
    if (this.editState) {
      return;
    }

    this.selectedPhaseIndex = phaseIndex;
    // Reset milestone selection when phase changes
    this.selectedMilestoneIndex = 0;
  }

  onMilestoneClick(milestoneIndex: number) {
    // Check if there's an invalid edit in progress
    if (this.editState) {
      return;
    }

    this.selectedMilestoneIndex = milestoneIndex;
  }

  get selectedPhase(): Phase | null {
    if (this.selectedPhaseIndex === null || !this.phases[this.selectedPhaseIndex]) {
      return null;
    }
    return this.phases[this.selectedPhaseIndex];
  }

  get selectedMilestone() {
    if (!this.selectedPhase || this.selectedMilestoneIndex === null) {
      return null;
    }
    const milestones = this.selectedPhase.milestones || [];
    return milestones[this.selectedMilestoneIndex] || null;
  }

  isEditing(type: string, phaseIndex: number, milestoneIndex?: number, taskIndex?: number): boolean {
    return (
      this.editState?.type === type &&
      this.editState?.phaseIndex === phaseIndex &&
      this.editState?.milestoneIndex === milestoneIndex &&
      this.editState?.taskIndex === taskIndex
    );
  }

  startEdit(type: 'phase' | 'milestone' | 'task', phaseIndex: number, milestoneIndex?: number, taskIndex?: number) {
    // Check if there's already an edit in progress and validate it first
    if (this.editState) {
      return;
    }

    // Store the original value before editing
    let originalValue: Phase | Milestone | TaskPackage | undefined;
    switch (type) {
      case 'phase':
        const originalPhase = this.phases[phaseIndex];
        originalValue = { ...originalPhase };
        if (originalPhase.milestones) {
          (originalValue as Phase).milestones = originalPhase.milestones.map(m => ({
            ...m,
            taskPackages: m.taskPackages ? m.taskPackages.map(tp => ({ ...tp })) : undefined,
          }));
        }
        break;
      case 'milestone':
        const originalMilestone = this.phases[phaseIndex]?.milestones?.[milestoneIndex!];
        if (originalMilestone) {
          originalValue = {
            ...originalMilestone,
            taskPackages: originalMilestone.taskPackages
              ? originalMilestone.taskPackages.map(tp => ({ ...tp }))
              : undefined,
          };
        }
        break;
      case 'task':
        const taskPackage = this.phases[phaseIndex]?.milestones?.[milestoneIndex!]?.taskPackages?.[taskIndex!];
        if (taskPackage) {
          originalValue = { ...taskPackage };
        }
        break;
    }

    this.editState = {
      type,
      phaseIndex,
      milestoneIndex,
      taskIndex,
      originalValue,
    };
    this.cdr.detectChanges();
  }

  startAdd(type: 'phase' | 'milestone' | 'task', phaseIndex?: number, milestoneIndex?: number) {
    // Check if there's already an edit in progress and validate it first
    if (this.editState) {
      return;
    }

    // Add the item directly to the list with default values
    switch (type) {
      case 'phase':
        this.phases.push({
          id: null,
          title: '',
          showInGantt: true,
          milestones: [],
        });
        this.editState = {
          type,
          phaseIndex: this.phases.length - 1,
          isNew: true, // Mark as new so it gets removed on cancel
        };
        break;
      case 'milestone':
        const phase = this.phases[phaseIndex!];
        if (phase) {
          if (!phase.milestones) phase.milestones = [];
          phase.milestones.push({
            id: null,
            name: '',
            weight: 0,
            taskPackages: [],
          });
          this.editState = {
            type,
            phaseIndex: phaseIndex!,
            milestoneIndex: phase.milestones.length - 1,
            isNew: true,
          };
        }
        break;
      case 'task':
        const milestone = this.phases[phaseIndex!]?.milestones?.[milestoneIndex!];
        if (milestone) {
          if (!milestone.taskPackages) milestone.taskPackages = [];
          milestone.taskPackages.push({ id: null, name: '' });
          this.editState = {
            type,
            phaseIndex: phaseIndex!,
            milestoneIndex: milestoneIndex!,
            taskIndex: milestone.taskPackages.length - 1,
            isNew: true,
          };
        }
        break;
    }
    this.phasesChange.emit([...this.phases]);
    this.cdr.detectChanges();
  }

  saveEdit(editingValue, value) {
    if (!this.multiStepForm?.currentStepIsValid()) return;
    Object.assign(value, editingValue);

    this.editState = null;
    this.cdr.detectChanges();
  }

  cancelEdit() {
    if (this.editState) {
      if (this.editState.isNew) {
        // For new items (added with startAdd), remove them from the list
        this.deleteItem(
          this.editState.type,
          this.editState.phaseIndex,
          this.editState.milestoneIndex,
          this.editState.taskIndex
        );
      }
    }
    this.editState = null;
    this.cdr.detectChanges();
  }

  deleteItem(type: 'phase' | 'milestone' | 'task', phaseIndex: number, milestoneIndex?: number, taskIndex?: number) {
    switch (type) {
      case 'phase':
        this.phases.splice(phaseIndex, 1);
        if (this.selectedPhaseIndex !== null && this.selectedPhaseIndex >= phaseIndex) {
          this.selectedPhaseIndex = Math.max(0, this.selectedPhaseIndex - 1);
          this.selectedMilestoneIndex = 0;
        }
        break;
      case 'milestone':
        const phase = this.phases[phaseIndex];
        if (phase?.milestones && milestoneIndex !== undefined) {
          phase.milestones.splice(milestoneIndex, 1);
          if (
            this.selectedPhaseIndex === phaseIndex &&
            this.selectedMilestoneIndex !== null &&
            this.selectedMilestoneIndex >= milestoneIndex
          ) {
            this.selectedMilestoneIndex = Math.max(0, this.selectedMilestoneIndex - 1);
          }
        }
        break;
      case 'task':
        const milestone = this.phases[phaseIndex]?.milestones?.[milestoneIndex!];
        if (milestone?.taskPackages && taskIndex !== undefined) {
          milestone.taskPackages.splice(taskIndex, 1);
        }
        break;
    }
    this.phasesChange.emit([...this.phases]);
    this.cdr.detectChanges();
  }

  startReorder(event: any) {
    if (this.editState) {
      event.cancel = true;
      return;
    }
    const { fromIndex, fromData } = event;
    event.itemData = fromData[fromIndex];
  }

  onDragMove(event: DragChangeEvent) {
    event.cancel = false;

    const targetType = event.toComponent.element().getAttribute('type');
    const type = event.fromComponent.element().getAttribute('type');
    if (
      (event.dropInsideItem == true &&
        ((targetType != 'milestone' && type == 'task-package') ||
          (targetType != 'phase' && type == 'milestone') ||
          type == 'phase')) ||
      (event.dropInsideItem == false && targetType != type)
    ) {
      event.cancel = true;
      console.log('cancel', { drop: event.dropInsideItem, targetType: targetType, type: type });
      return;
    }
    console.log('continue', { drop: event.dropInsideItem, targetType: targetType, type: type });
  }

  onReorder(event: ReorderEvent) {
    const { fromIndex, toIndex, fromData, toData, itemData, dropInsideItem, toComponent, fromComponent } = event;
    const targetType = toComponent.element().getAttribute('type');
    const type = fromComponent.element().getAttribute('type');

    if (fromIndex === toIndex && !dropInsideItem) return;
    fromData.splice(fromIndex, 1);
    if (!dropInsideItem) {
      toData.splice(toIndex, 0, itemData);
    } else {
      switch (targetType) {
        case 'phase':
          if (!toData[toIndex].milestones) toData[toIndex].milestones = [];
          toData[toIndex].milestones.push(itemData);
          itemData.phaseId = toData[toIndex].id;
          itemData.sortingIndex = toData[toIndex].milestones.length;
          break;
        case 'milestone':
          console.log('milestone', toData, toIndex, itemData);
          if (!toData[toIndex].taskPackages) toData[toIndex].taskPackages = [];
          toData[toIndex].taskPackages.push(itemData);
          itemData.milestoneId = toData[toIndex].id;
          itemData.sortingIndex = toData[toIndex].taskPackages.length;
          break;
      }
    }

    this.phasesChange.emit([...this.phases]);
    this.cdr.detectChanges();
  }

  getPhaseValidationCallback(phaseIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      return !this.phases.some(
        (phase, index) => index !== phaseIndex && phase.title?.trim()?.toLowerCase() === trimmedName
      );
    };
  }

  getMilestoneValidationCallback(phaseIndex: number, milestoneIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      // Check across all phases for unique milestone names
      return !this.phases.some(phase =>
        phase.milestones?.some((milestone, index) => {
          // Skip current milestone being edited
          if (phase === this.phases[phaseIndex] && index === milestoneIndex) {
            return false;
          }
          return milestone.name?.trim()?.toLowerCase() === trimmedName;
        })
      );
    };
  }

  getTaskValidationCallback(phaseIndex: number, milestoneIndex: number, taskIndex: number) {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      // Check across all phases and milestones for unique task package names
      return !this.phases.some(phase =>
        phase.milestones?.some(milestone =>
          milestone.taskPackages?.some((taskPackage, index) => {
            // Skip current task package being edited
            if (
              phase === this.phases[phaseIndex] &&
              milestone === phase.milestones![milestoneIndex] &&
              index === taskIndex
            ) {
              return false;
            }
            return taskPackage.name?.trim()?.toLowerCase() === trimmedName;
          })
        )
      );
    };
  }

  getNewItemValidationCallback(type: 'phase' | 'milestone' | 'task') {
    return (value: string) => {
      const trimmedName = value.trim().toLowerCase();
      if (!trimmedName) return false;

      switch (type) {
        case 'phase':
          return !this.phases.some(phase => phase.title?.trim()?.toLowerCase() === trimmedName);

        case 'milestone':
          return !this.phases.some(phase =>
            phase.milestones?.some(milestone => milestone.name?.trim()?.toLowerCase() === trimmedName)
          );

        case 'task':
          return !this.phases.some(phase =>
            phase.milestones?.some(milestone =>
              milestone.taskPackages?.some(taskPackage => taskPackage.name?.trim()?.toLowerCase() === trimmedName)
            )
          );

        default:
          return true;
      }
    };
  }
}
